(() => {
  if (customElements.get('sam-story-timeline')) return;

  class SamStoryTimeline extends HTMLElement {
    connectedCallback() {
      this.chapters = [...this.querySelectorAll('.sam-timeline__chapter')];
      this.paths = this.querySelector('[data-timeline-paths]');
      this.timers = [];
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 999px)');
      if (!this.chapters.length || !this.paths) return;

      this.section = this.closest('.sam-timeline');
      this.viewport = this.querySelector('.sam-timeline__viewport');
      if (window.Shopify?.designMode) return;
      this.section.classList.add('is-pinned');
      this.onScroll = () => {
        if (!this.snapping) this.queueSnap();
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.updateProgress();
        });
      };
      this.onResize = () => { this.cancelSnap(); this.measureScroll(); };
      this.onInput = () => { this.cancelSnap(); this.queueSnap(); };
      this.onTouchStart = (event) => {
        this.touching = true;
        this.cancelSnap();
        this.beginSwipe(event);
      };
      this.onTouchMove = (event) => this.moveSwipe(event);
      this.onTouchEnd = (event) => {
        this.touching = event.touches.length > 0;
        if (!this.touching) {
          this.endSwipe(event.type === 'touchcancel');
          this.queueSnap();
        }
      };
      this.onHorizontalWheel = (event) => {
        if (event.ctrlKey || Math.abs(event.deltaX) <= Math.abs(event.deltaY) || !this.isPinned()) return;
        event.preventDefault();
        this.cancelSnap();
        const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.viewport.clientWidth : 1;
        this.moveTo(this.distance() + event.deltaX * unit);
        this.queueSnap();
      };
      this.viewport.addEventListener('touchmove', this.onTouchMove, { passive: false });
      this.viewport.addEventListener('wheel', this.onHorizontalWheel, { passive: false });
      window.addEventListener('wheel', this.onInput, { passive: true });
      window.addEventListener('pointerdown', this.onInput, { passive: true });
      window.addEventListener('keydown', this.onInput);
      window.addEventListener('touchstart', this.onTouchStart, { passive: true });
      window.addEventListener('touchend', this.onTouchEnd, { passive: true });
      window.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.resize = new ResizeObserver(() => { this.drawArrows(); this.measureScroll(); });
      this.resize.observe(this);
      this.chapters.forEach((chapter) => this.resize.observe(chapter));

      if (!this.motion.matches && 'IntersectionObserver' in window) {
        this.classList.add('is-ready');
        this.observer = new IntersectionObserver((entries) => {
          entries.filter((entry) => entry.isIntersecting)
            .sort((a, b) => this.chapters.indexOf(a.target) - this.chapters.indexOf(b.target))
            .forEach((entry, index) => {
              this.observer.unobserve(entry.target);
              this.timers.push(setTimeout(() => {
                entry.target.classList.add('is-visible');
                this.paths.children[this.chapters.indexOf(entry.target)]?.classList.add('is-visible');
              }, index * 180));
            });
        }, { threshold: 0.12, rootMargin: '0px 0px -32px 0px' });
        this.chapters.forEach((chapter) => this.observer.observe(chapter));
      }
      this.drawArrows();
      this.measureScroll();
    }

    measureScroll() {
      this.travel = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const height = this.getBoundingClientRect().height;
      this.pinTop = Math.min(0, window.innerHeight - height);
      this.section.style.setProperty('--timeline-pin-top', `${this.pinTop}px`);
      this.section.style.height = `${Math.ceil(height + this.travel)}px`;
      this.updateProgress();
    }

    updateProgress() {
      // A vertical runway keeps the section pinned until its last chapter is reached.
      // Native document scrolling still handles touch, keyboard, and scrolling back up.
      const distance = this.pinTop - this.section.getBoundingClientRect().top;
      this.viewport.scrollLeft = Math.max(0, Math.min(this.travel, distance));
    }

    distance() {
      return this.pinTop - this.section.getBoundingClientRect().top;
    }

    isPinned() {
      const distance = this.distance();
      return this.travel > 0 && distance >= -1 && distance <= this.travel + 1;
    }

    moveTo(distance) {
      const target = Math.max(0, Math.min(this.travel, distance));
      window.scrollTo({ top: window.scrollY + target - this.distance(), behavior: 'instant' });
      this.updateProgress();
    }

    beginSwipe(event) {
      this.swipe = null;
      if (event.touches.length !== 1 || !this.viewport.contains(event.target) || !this.isPinned()) return;
      const touch = event.touches[0];
      this.swipe = { id: touch.identifier, x: touch.clientX, y: touch.clientY, start: this.distance(), dx: 0, axis: null };
    }

    moveSwipe(event) {
      const swipe = this.swipe;
      if (!swipe) return;
      if (event.touches.length !== 1) { this.swipe = null; return; }
      const touch = event.touches[0];
      if (touch.identifier !== swipe.id) return;
      const dx = swipe.x - touch.clientX;
      const dy = swipe.y - touch.clientY;
      if (!swipe.axis) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 8) return;
        swipe.axis = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
      }
      if (swipe.axis !== 'horizontal') return;
      event.preventDefault();
      swipe.dx = dx;
      this.moveTo(swipe.start + dx);
    }

    chapterStops() {
      const first = this.chapters[0].offsetLeft;
      return this.chapters.map(chapter => Math.max(0, Math.min(this.travel, chapter.offsetLeft - first)));
    }

    endSwipe(cancelled) {
      const swipe = this.swipe;
      this.swipe = null;
      if (!swipe || swipe.axis !== 'horizontal') return;
      const stops = this.chapterStops();
      const distance = this.distance();
      let target = stops.reduce((best, stop) => Math.abs(stop - distance) < Math.abs(best - distance) ? stop : best);
      // A deliberate short swipe still changes chapters; a longer drag can cover several.
      if (!cancelled && Math.abs(swipe.dx) >= 32) {
        if (swipe.dx > 0) target = Math.max(target, stops.find(stop => stop > swipe.start + 1) ?? this.travel);
        else target = Math.min(target, [...stops].reverse().find(stop => stop < swipe.start - 1) ?? 0);
      }
      this.settleAt(target);
    }

    queueSnap() {
      clearTimeout(this.snapTimer);
      if (!this.mobile.matches || this.touching || this.snapping) return;
      // Wait for both the finger and native momentum to come to rest.
      this.snapTimer = setTimeout(() => this.snapToNearest(), 180);
    }

    cancelSnap() {
      clearTimeout(this.snapTimer);
      cancelAnimationFrame(this.snapFrame);
      this.snapping = false;
    }

    snapToNearest() {
      if (!this.mobile.matches || this.touching || this.snapping || !this.travel) return;
      const distance = this.pinTop - this.section.getBoundingClientRect().top;
      // Do not pull the page back after the pinned section has released.
      if (distance <= 0 || distance >= this.travel) return;
      const stops = this.chapterStops();
      const nearest = stops.reduce((best, stop) =>
        Math.abs(stop - distance) < Math.abs(best - distance) ? stop : best);
      this.settleAt(nearest);
    }

    settleAt(target) {
      const delta = target - this.distance();
      if (Math.abs(delta) < 1) return;
      const startY = window.scrollY;
      if (this.motion.matches) {
        window.scrollTo({ top: startY + delta, behavior: 'instant' });
        this.updateProgress();
        return;
      }
      this.snapping = true;
      const started = performance.now();
      const settle = (now) => {
        const elapsed = (now - started) / 1000;
        // Critically damped spring: a quick catch, then a soft landing without overshoot.
        const remaining = (1 + 14 * elapsed) * Math.exp(-14 * elapsed);
        const done = Math.abs(delta * remaining) < 0.5 || elapsed > 0.9;
        window.scrollTo({ top: startY + delta * (done ? 1 : 1 - remaining), behavior: 'instant' });
        this.updateProgress();
        if (done) {
          this.snapping = false;
          this.snapFrame = null;
        } else {
          this.snapFrame = requestAnimationFrame(settle);
        }
      };
      this.snapFrame = requestAnimationFrame(settle);
    }

    drawArrows() {
      // Layout offsets avoid measuring the moving reveal transform.
      const boxes = this.chapters.map((chapter) => {
        const photo = chapter.querySelector('.sam-timeline__photo');
        return { x: chapter.offsetLeft + photo.offsetLeft, y: chapter.offsetTop,
          w: photo.offsetWidth, h: photo.offsetHeight,
          bottom: chapter.offsetTop + chapter.offsetHeight };
      });
      this.paths.replaceChildren();
      boxes.slice(0, -1).forEach((from, index) => {
        const to = boxes[index + 1];
        const x1 = from.x + from.w + 12;
        const x2 = to.x - 14;
        const y1 = from.y + from.h / 2;
        const y2 = to.y + to.h / 2;
        const middle = (x1 + x2) / 2;
        const d = `M${x1} ${y1} C${middle} ${y1 - 25},${middle} ${y2 + 25},${x2} ${y2}`;
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', d);
        path.setAttribute('marker-end', `url(#${this.paths.dataset.marker})`);
        path.setAttribute('class', 'sam-timeline__arrow');
        if (this.chapters[index].classList.contains('is-visible')) path.classList.add('is-visible');
        this.paths.append(path);
      });
    }

    disconnectedCallback() {
      this.cancelSnap();
      window.removeEventListener('wheel', this.onInput);
      window.removeEventListener('pointerdown', this.onInput);
      window.removeEventListener('keydown', this.onInput);
      window.removeEventListener('touchstart', this.onTouchStart);
      window.removeEventListener('touchend', this.onTouchEnd);
      window.removeEventListener('touchcancel', this.onTouchEnd);
      this.viewport?.removeEventListener('touchmove', this.onTouchMove);
      this.viewport?.removeEventListener('wheel', this.onHorizontalWheel);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      cancelAnimationFrame(this.frame);
      this.section?.classList.remove('is-pinned');
      this.section?.style.removeProperty('height');
      this.observer?.disconnect();
      this.resize?.disconnect();
      this.timers.forEach(clearTimeout);
    }
  }
  customElements.define('sam-story-timeline', SamStoryTimeline);
})();
